import React, { useState, useRef, useEffect } from "react";
import { theme, fontMono, fontBody, SOURCE_META, WORK_TYPE_META } from "./theme";

/* ── TagInput ─────────────────────────────────────────────────────── */
export function TagInput({ label, tags, onChange, suggestions = [], placeholder, onQueryChange }) {
  const [input, setInput]       = useState("");
  const [open, setOpen]         = useState(false);
  const [focused, setFocused]   = useState(false);
  const wrapRef                 = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = suggestions.filter(
    s => s.toLowerCase().includes(input.toLowerCase()) && !tags.includes(s)
  ).slice(0, 10);

  function addTag(val) {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput("");
    setOpen(false);
  }

  function removeTag(t) {
    onChange(tags.filter(x => x !== t));
  }

  function handleKey(e) {
    if ((e.key === "Enter" || e.key === ",") && input.trim()) {
      e.preventDefault();
      addTag(input);
    }
    if (e.key === "Backspace" && !input && tags.length) {
      removeTag(tags[tags.length - 1]);
    }
  }

  return (
    <div style={{ marginBottom: 22 }} ref={wrapRef}>
      <div style={{
        fontSize: 11.5, fontWeight: 700, color: theme.textDim,
        letterSpacing: "1.4px", textTransform: "uppercase",
        marginBottom: 10, fontFamily: fontMono,
      }}>
        {label}
      </div>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
        padding: "8px 10px", minHeight: 44,
        border: `1px solid ${focused ? theme.borderHi : theme.border}`,
        borderRadius: 10, background: theme.bgInset,
        boxShadow: focused ? "0 0 0 3px rgba(201,164,104,0.12)" : "none",
        cursor: "text",
      }} onClick={() => wrapRef.current.querySelector("input").focus()}>
        {tags.map(t => (
          <span key={t} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: theme.goldSoft, color: theme.goldText,
            padding: "3px 10px 3px 10px", borderRadius: 6,
            fontSize: 12, fontWeight: 600, fontFamily: fontMono,
            border: `1px solid ${theme.border}`,
          }}>
            {t}
            <button onClick={e => { e.stopPropagation(); removeTag(t); }} style={{
              background: "none", border: "none", cursor: "pointer",
              color: theme.textFaint, fontSize: 14, lineHeight: 1,
              padding: 0, display: "flex", alignItems: "center",
            }}>×</button>
          </span>
        ))}
        <input
          value={input}
          onChange={e => {
            setInput(e.target.value);
            setOpen(true);
            if (onQueryChange) onQueryChange(e.target.value);
          }}
          onFocus={() => { setFocused(true); setOpen(true); if (onQueryChange) onQueryChange(input); }}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKey}
          placeholder={tags.length ? "" : placeholder}
          style={{
            border: "none", outline: "none", background: "transparent",
            color: theme.text, fontSize: 13, fontFamily: fontBody,
            flex: 1, minWidth: 140,
          }}
        />
      </div>
      {open && filtered.length > 0 && (
        <div style={{
          position: "relative", zIndex: 200,
        }}>
          <div style={{
            position: "absolute", top: 4, left: 0, right: 0,
            background: theme.bgRaised, border: `1px solid ${theme.border}`,
            borderRadius: 10, overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          }}>
            {filtered.map(s => (
              <div key={s}
                onMouseDown={e => { e.preventDefault(); addTag(s); }}
                style={{
                  padding: "9px 14px", fontSize: 13, cursor: "pointer",
                  color: theme.text, transition: "background 0.15s",
                  borderBottom: `1px solid ${theme.border}`,
                }}
                onMouseEnter={e => e.currentTarget.style.background = theme.goldSoft}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── JobCard ──────────────────────────────────────────────────────── */
export function JobCard({ job, highlight }) {
  const wt    = (job.work_type || "any").toLowerCase();
  const wtm   = WORK_TYPE_META[wt] || WORK_TYPE_META.any;
  const sm    = SOURCE_META[job.source] || {};
  const exp   = job.exp_label || (job.min_exp != null ? `${job.min_exp}–${job.max_exp} yrs` : "");

  return (
    <div className={`tt-hover-lift${highlight ? " tt-gold-flash" : ""}`} style={{
      background: theme.bgRaised,
      border: `1px solid ${highlight ? theme.borderHi : theme.border}`,
      borderRadius: 12, padding: "16px 18px",
      transition: "border-color 0.3s ease",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 5 }}>
            <span style={{
              fontSize: 14, fontWeight: 700, color: theme.text,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {job.title}
            </span>
            {job.is_early_applicant ? (
              <span style={{ fontSize: 10.5, fontWeight: 700, background: theme.emeraldBg, color: theme.emerald, padding: "2px 8px", borderRadius: 5, border: `1px solid rgba(52,211,153,0.3)`, whiteSpace: "nowrap" }}>Early</span>
            ) : null}
            {job.is_easy_apply ? (
              <span style={{ fontSize: 10.5, fontWeight: 700, background: theme.amberBg, color: theme.amber, padding: "2px 8px", borderRadius: 5, border: `1px solid rgba(245,185,110,0.3)`, whiteSpace: "nowrap" }}>Quick Apply</span>
            ) : null}
            {job.is_recruiter_post ? (
              <span style={{ fontSize: 10.5, fontWeight: 700, background: "rgba(124,58,237,0.10)", color: "#a78bfa", padding: "2px 8px", borderRadius: 5, border: "1px solid rgba(124,58,237,0.3)", whiteSpace: "nowrap" }}>Recruiter</span>
            ) : null}
          </div>
          <div style={{ fontSize: 12.5, color: theme.textDim, fontWeight: 500, marginBottom: 8 }}>
            {job.company}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            {job.location && (
              <span style={{ fontSize: 11.5, color: theme.textFaint, fontFamily: fontMono }}>
                📍 {job.location}
              </span>
            )}
            {exp && (
              <span style={{ fontSize: 11.5, color: theme.textFaint, fontFamily: fontMono }}>
                🕐 {exp}
              </span>
            )}
            {job.package && (
              <span style={{ fontSize: 11.5, color: theme.textFaint, fontFamily: fontMono }}>
                💰 {job.package}
              </span>
            )}
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 5,
              color: wtm.color, background: wtm.bg,
            }}>
              {wtm.label}
            </span>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 5,
              color: sm.color || theme.textDim, background: sm.bg || theme.bgInset,
            }}>
              {job.source}
            </span>
          </div>
        </div>
        {job.url && (
          <a href={job.url} target="_blank" rel="noopener noreferrer" style={{
            display: "inline-flex", alignItems: "center",
            padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: theme.gold, color: "#1a1306",
            textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0,
            transition: "opacity 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            Apply →
          </a>
        )}
      </div>
    </div>
  );
}

/* ── SkippedRow ───────────────────────────────────────────────────── */
export function SkippedRow({ item }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "8px 10px", borderRadius: 8,
      background: theme.bgInset, border: `1px solid ${theme.border}`,
    }}>
      <span style={{
        fontSize: 10.5, fontWeight: 700, color: theme.garnet,
        background: theme.garnetBg, padding: "2px 7px", borderRadius: 4,
        whiteSpace: "nowrap", marginTop: 1, flexShrink: 0,
      }}>
        {item.source}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.textDim, marginBottom: 2 }}>
          {item.title} {item.company ? `— ${item.company}` : ""}
        </div>
        <div style={{ fontSize: 11.5, color: theme.textFaint }}>
          {item.reason}
        </div>
      </div>
    </div>
  );
}